import React, { useState } from 'react';
import axiosAuth from '../../../config/axios';
import { diaLocal, estilos as s } from '../comun';

// Pestaña "Asistente IA" del caso. Todo lo que devuelve es un BORRADOR: no se
// guarda en ninguna parte; el orientador lo copia si le sirve.
// El servidor envía los registros seudonimizados y nunca las notas privadas.

const ACCIONES = [
  { id: 'resumen', nombre: 'Resumen del caso', desc: 'Cronología: cómo llegó, qué se ha hecho y en qué va.' },
  { id: 'avances', nombre: 'Avances del período', desc: 'Acciones, compromisos y citas entre dos fechas.' },
  { id: 'sugerencias', nombre: 'Sugerencias de seguimiento', desc: 'Ideas operativas según fechas y pendientes.' },
  { id: 'patrones', nombre: 'Describir datos académicos', desc: 'Pone en palabras las cifras de notas y asistencia.' },
  { id: 'redactar', nombre: 'Ayuda de redacción', desc: 'Convierte tus ideas en un texto claro.' },
];

const DOCUMENTOS = {
  informe: 'Informe de seguimiento',
  compromiso: 'Acta de compromisos',
  comunicacion_familia: 'Comunicación para la familia',
  plan: 'Objetivo y acciones de un plan',
};

export default function AsistenteIA({ caso }) {
  const hace30 = new Date(); hace30.setDate(hace30.getDate() - 30);
  const [accion, setAccion] = useState('resumen');
  const [desde, setDesde] = useState(diaLocal(hace30));
  const [hasta, setHasta] = useState(diaLocal(new Date()));
  const [texto, setTexto] = useState('');
  const [tipoDoc, setTipoDoc] = useState('informe');
  const [resultado, setResultado] = useState(null);
  const [verEnviado, setVerEnviado] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [error, setError] = useState('');

  async function generar() {
    setOcupado(true); setError(''); setResultado(null); setVerEnviado(false);
    const cuerpo = { accion };
    if (accion === 'avances') Object.assign(cuerpo, { desde, hasta });
    if (accion === 'redactar') Object.assign(cuerpo, { texto, tipo_documento: tipoDoc });
    try {
      const r = await axiosAuth.post(`/api/bienestar/ia/${caso.id}`, cuerpo);
      setResultado(r.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'El asistente no está disponible en este momento');
    } finally {
      setOcupado(false);
    }
  }

  async function copiar() {
    try {
      await navigator.clipboard.writeText(resultado.borrador);
      setCopiado(true); setTimeout(() => setCopiado(false), 2000);
    } catch { /* el texto sigue visible para copiarlo a mano */ }
  }

  const listo = accion !== 'redactar' || texto.trim().length >= 10;

  return (
    <div>
      <div style={es.aviso}>
        <strong>Asistente de apoyo, no de diagnóstico.</strong> Organiza y redacta a partir de los registros del caso.
        Antes de enviarlos, Playfesor reemplaza los nombres (“el estudiante”, “Acudiente 1”…) y quita teléfonos, correos y
        documentos. <strong>Las notas privadas nunca se envían.</strong> Todo lo que produce es un borrador que debes revisar.
      </div>

      <div style={es.acciones} role="radiogroup" aria-label="Qué necesitas">
        {ACCIONES.map(a => (
          <button key={a.id} role="radio" aria-checked={accion === a.id} onClick={() => { setAccion(a.id); setResultado(null); setError(''); }}
            style={{ ...es.accion, ...(accion === a.id ? es.accionActiva : {}) }}>
            <span style={es.accionNombre}>{a.nombre}</span>
            <span style={es.accionDesc}>{a.desc}</span>
          </button>
        ))}
      </div>

      {accion === 'avances' && (
        <div style={es.fila}>
          <div style={s.campo}>
            <label style={s.label}>Desde</label>
            <input type="date" style={s.input} value={desde} max={hasta} onChange={e => setDesde(e.target.value)} />
          </div>
          <div style={s.campo}>
            <label style={s.label}>Hasta</label>
            <input type="date" style={s.input} value={hasta} min={desde} onChange={e => setHasta(e.target.value)} />
          </div>
        </div>
      )}
      {accion === 'redactar' && (
        <>
          <div style={s.campo}>
            <label style={s.label}>Tipo de texto</label>
            <select style={{ ...s.input, maxWidth: '320px' }} value={tipoDoc} onChange={e => setTipoDoc(e.target.value)}>
              {Object.entries(DOCUMENTOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div style={s.campo}>
            <label style={s.label}>Tus ideas o tu borrador</label>
            <textarea style={{ ...s.input, minHeight: '110px', resize: 'vertical' }} maxLength={6000} value={texto}
              onChange={e => setTexto(e.target.value)} placeholder="Escribe los puntos que quieres incluir. Hechos, no conclusiones." />
          </div>
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
        <button onClick={generar} disabled={ocupado || !listo} style={{ ...s.btnPrimario, opacity: listo ? 1 : 0.5 }}>
          {ocupado ? 'Generando borrador...' : 'Generar borrador'}
        </button>
      </div>

      {error && <div style={s.errorBox}>{error}</div>}

      {resultado && (
        <div style={es.resultado}>
          <div style={es.resultadoCabecera}>
            <span style={es.chipBorrador}>BORRADOR · {resultado.accion}</span>
            <button onClick={copiar} style={es.btnMini}>{copiado ? 'Copiado ✓' : 'Copiar texto'}</button>
          </div>
          <div style={es.borrador}>{resultado.borrador}</div>
          <p style={es.pista}>
            Revísalo y corrígelo antes de usarlo. No se guardó en el caso: si te sirve, cópialo en un seguimiento o documento.
            {resultado.seguimientos_omitidos > 0 && ` Se usaron solo los seguimientos más recientes (${resultado.seguimientos_omitidos} más antiguos no se incluyeron).`}
          </p>
          <button onClick={() => setVerEnviado(!verEnviado)} style={es.enlace}>
            {verEnviado ? 'Ocultar lo que se envió' : 'Ver exactamente qué se envió al asistente'}
          </button>
          {verEnviado && <pre style={es.enviado}>{resultado.enviado}</pre>}
        </div>
      )}
    </div>
  );
}

const es = {
  aviso: { background: '#f3f1ff', border: '1px solid #e0dcff', color: '#4a4380', borderRadius: '12px', padding: '12px 14px', fontSize: '13px', lineHeight: 1.55, marginBottom: '14px' },
  acciones: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(190px, 100%), 1fr))', gap: '8px', marginBottom: '14px' },
  accion: { display: 'flex', flexDirection: 'column', gap: '3px', textAlign: 'left', background: '#fff', border: '1.5px solid #e6e8f0', borderRadius: '12px', padding: '10px 12px', cursor: 'pointer', fontFamily: 'inherit' },
  accionActiva: { border: '1.5px solid #667eea', background: '#f5f6ff' },
  accionNombre: { fontSize: '13.5px', fontWeight: 700, color: '#1a1a2e' },
  accionDesc: { fontSize: '12px', color: '#888', lineHeight: 1.4 },
  fila: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: '12px', maxWidth: '420px' },
  resultado: { border: '1px dashed #c9c4f5', background: '#fcfbff', borderRadius: '12px', padding: '14px 16px' },
  resultadoCabecera: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' },
  chipBorrador: { background: '#fff8e1', color: '#8a5a00', borderRadius: '999px', padding: '3px 10px', fontSize: '11.5px', fontWeight: 800 },
  btnMini: { background: '#fff', border: '1px solid #dde0f0', color: '#667eea', borderRadius: '8px', padding: '6px 12px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  borrador: { fontSize: '14px', color: '#333', lineHeight: 1.65, whiteSpace: 'pre-wrap' },
  pista: { fontSize: '12px', color: '#999', margin: '12px 0 6px' },
  enlace: { background: 'none', border: 'none', color: '#667eea', cursor: 'pointer', fontSize: '12.5px', fontWeight: 600, padding: 0, fontFamily: 'inherit' },
  enviado: { background: '#f7f7fa', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: '#555', whiteSpace: 'pre-wrap', maxHeight: '280px', overflowY: 'auto', marginTop: '8px' },
};
